import React, { useCallback, useMemo, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import cloudKeysApi from 'src/Libs/installation-rights-management/api/cloud-keys.api'
import { CloudRegistryKeyActionMenu } from 'src/Libs/installation-rights-management/components/cloud-registry-keys'
import { Stack, useMediaQuery, useTheme } from '@mui/material'
import SecureDataField from 'src/Elements/Data/SecureDataField'
import { format } from '@e3dc-react/shell/Libs/date'
import { CloudKeyModel } from 'src/Libs/installation-rights-management/models'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import useCloudKeys from 'src/Libs/installation-rights-management/hooks/use-cloud-keys'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { ExtensionsType } from 'src/Libs/api/types'
import { isDirectMarketingKeyInUse } from 'src/Libs/installation-extensions/utils/luox'
import { SERVICE_LEVELS_ALL } from 'src/Configuration'
import { useTypedSelector } from 'src/State/RootReducer'

interface HeaderData {
  label: string
  key: string
}

interface UseCloudKeysTableHook {
  /**
   * Load / reload all available CloudKey for an initial provided Installation-reference.
   * It also triggers the re-generation of the entries from `header`, `data` and `actionMenus`
   */
  refreshData: () => Promise<void>

  /**
   * Information for the head of an DefaultTable-component
   */
  header: HeaderData[]

  /**
   * A list of pre-configured ReactNode items to be displayed in a DefaultTable component.
   * It contains a 2-multilevel list which represents the rows and columns of a table.
   */
  data: React.ReactNode[][]

  /**
   * All the relevant action menus needed for each single row provided in the `data`-prop, to be used in a DefaultTable component.
   */
  actionMenus: React.ReactNode[]

  /**
   * Indicates whether the CloudKey data is currently being loaded.
   */
  isLoading: boolean
}

/**
 * Providing the table-configuration and management-functions for the CloudKey table of this module.
 *
 * This hook is intended be used as a support to populate a DefaultTable component.
 *
 * @param installation
 */
const useCloudKeysTable = (installation: Installation): UseCloudKeysTableHook => {
  const { breakpoints } = useTheme()
  const isShortTable = useMediaQuery(breakpoints.down('sm'))
  const { t } = useCustomTranslation()
  const { removeCloudKey, updateCloudKey } = useCloudKeys()
  const [rawData, setRawData] = useState<CloudKeyModel[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const authFetch = useAuthFetchWithResponse()
  const userLevel = useTypedSelector(state => state.user.userData?.level)
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const tableHeader = useMemo<HeaderData[]>(() => {
    const headers = [
      t('installation.cloud-registry-keys.table.header.columns.name'),
      t('installation.cloud-registry-keys.table.header.columns.keys'),
      t('installation.cloud-registry-keys.table.header.columns.created-at'),
    ].map(elem => ({
      label: t(elem),
      key: elem,
    }))

    if (isShortTable) {
      return headers.slice(0, 2)
    }

    return headers
  }, [isShortTable, t])

  const tableBody = useMemo<React.ReactNode[][]>(() => {
    const filteredData = rawData.filter(
      item => isDirectMarketingKeyInUse(item) || item.scope !== ExtensionsType.luoxEnergy || SERVICE_LEVELS_ALL.includes(userLevel || 'USER')
    )
    const rows = filteredData.map((item, index) => [
      item.description,
      <SecureDataField
        key={index}
        value={item.key}
        clipboardMessage={t('installation.cloud-registry-keys.keys.clipboard-success')}
        displayCopy={!isShortTable}
      />,
      format(new Date(item.createdAt), t('common.format.date-fns-date')),
    ])

    if (isShortTable) {
      return rows.map(row => row.slice(0, 2))
    }

    return rows
  }, [userLevel, isShortTable, rawData, t])
  const refreshData = useCallback(async (): Promise<void> => {
    if (!installation) {
      console.error('Invalid installation', installation)
      return
    }
    setIsLoading(true)
    return cloudKeysApi
      .findAll(installation.id, authFetch)
      .then(result => {
        const values = result.value || []
        setRawData(values)
      })
      .finally(() => setIsLoading(false))
  }, [authFetch, installation])
  const handleRemoveCloudKey = useCallback(
    (value: CloudKeyModel): void => {
      removeCloudKey(installation.id, value.id)
        .then(() => {
          refreshData().catch(console.error)
          enqueueSuccessStack('installation.cloud-registry-keys.remove-key.success')
        })
        .catch(() => enqueueErrorStack('installation.cloud-registry-keys.remove-key.failed'))
    },
    [enqueueErrorStack, enqueueSuccessStack, installation.id, refreshData, removeCloudKey]
  )

  const handleRenameCloudKey = useCallback(
    (value: CloudKeyModel, description: string): void => {
      updateCloudKey(installation.id, value.id, description)
        .then(() => {
          refreshData().catch(console.error)
          enqueueSuccessStack('installation.cloud-registry-keys.remove-key.success')
        })
        .catch(() => enqueueErrorStack('installation.cloud-registry-keys.remove-key.failed'))
    },
    [enqueueErrorStack, enqueueSuccessStack, installation.id, refreshData, updateCloudKey]
  )

  const buildActionMenu = useCallback(
    (item: CloudKeyModel, index: number): React.ReactNode => {
      return (
        <Stack key={index} direction={'row'} justifyContent={'flex-end'}>
          <CloudRegistryKeyActionMenu
            item={item}
            onRemove={handleRemoveCloudKey}
            onRename={handleRenameCloudKey}
            disable={item.isDeletable === false && item.scope === ExtensionsType.luoxEnergy}
            tooltipText={t('installation.cloud-registry-keys.disabled-tooltip')}
          />
        </Stack>
      )
    },
    [handleRemoveCloudKey, handleRenameCloudKey, t]
  )

  const actionMenus = useMemo<React.ReactNode[]>(() => {
    return rawData.map((item, index) => {
      return buildActionMenu(item, index)
    })
  }, [buildActionMenu, rawData])

  return { refreshData, header: tableHeader, data: tableBody, actionMenus, isLoading }
}

export default useCloudKeysTable
